/**
 * students/{id} — one Firestore doc per dancer.
 *
 * Minimal first cut per Bobbie's call:
 *   - firstName / lastName
 *   - dob              (ISO date YYYY-MM-DD — drives age & age-group sort)
 *   - joinedStudioOn   (ISO date — drives tenure / "years at the studio")
 *   - familyUids[]     (uids of parent/guardian user accounts who can see
 *                      this kid; Family Portal reads against this)
 *   - audit fields
 *
 * Enrollment (linking students to offerings) lives on the offering doc's
 * studentIds[] array; that wiring lands in the per-class roster PR.
 *
 * Rules: Director full access; family members linked via familyUids[]
 * can read their own kid (see shared/firestore.rules).
 */

import { useEffect, useMemo, useState } from 'react';
import { decodeHtmlEntities } from './decodeHtmlEntities';
import {
  addDoc,
  arrayRemove,
  arrayUnion,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
  writeBatch,
  type FieldValue,
  type Timestamp,
} from 'firebase/firestore';
import {
  getDownloadURL,
  ref as storageRef,
} from 'firebase/storage';
// Not firebase/storage directly: the wrapper marks an upload in flight so the
// version auto-update cannot reload the tab mid-send (see storageUpload.ts).
import { uploadBytes } from './storageUpload';
import { auth, db, storage } from './firebase';
import { functionHeaders } from './functionSecurity';
// The one server transaction that moves BOTH sides of the membership link.
// deleteStudent() below routes through it rather than improvising a second
// roster write here, so a delete and a roster edit cannot drift apart.
import { unenrollStudent } from './offerings';
// The one server lane that takes a Jr Staff designation back, demotes the
// dancer's own promoted login, and revokes its Firebase refresh tokens.
// deleteStudent() below routes through it rather than writing any of those
// three by hand, so a delete and an un-designate can never disagree.
import { setStudentJrStaffDesignation } from './jrStaffDesignations';
import { useAuth } from './auth-context';
import { isHiddenFixtureData } from './smokeFixture';
import { useReadEpoch } from './readEpoch';
import type { IsoDate } from './tenure';
import { isPermissionDenied, readRealRole, studentsReadViaLane, useJrStaffStudentRows, type JrStaffStudentRow } from './jrStaffReadsClient';

export interface StudentDoc {
  id: string;
  firstName: string;
  lastName: string;
  nickname?: string;
  preferredName?: string;
  dob: IsoDate;
  joinedStudioOn: IsoDate | null;
  /**
   * Comp-team membership flag. True = on the competition team this
   * season; false (default) = recreational only. Drives visibility in
   * the Family Portal (comp dancers see competition schedules, results,
   * costume cards, etc.). Per the scope doc this is the single source
   * of truth — never hardcode comp vs regular elsewhere.
   */
  onCompTeam: boolean;
  familyUids: string[];
  /** IDs of sibling students — kept in sync bidirectionally. */
  siblingIds: string[];
  /**
   * IDs of students a Director has confirmed are NOT the same person as
   * this one — kept in sync bidirectionally, same pattern as siblingIds.
   * Exists so the Possible duplicates card can be dismissed for a pair
   * that really is two different dancers (Bobbie, 2026-08-25: "I need a
   * way to dismiss as these are two seperate people"). Read only by the
   * duplicate finder; grants nothing and shows nowhere else.
   */
  notDuplicateIds?: string[];
  /** Offering IDs this student is enrolled in (mirror of offering.studentIds). */
  memberships: string[];
  /**
   * Optional dancer profile photo. Uploaded by a linked family account (or
   * staff) to dancer-photos/{studentId}/{uid}/... in Storage; rules let a
   * linked family change ONLY this field on their own dancer.
   */
  photoUrl?: string | null;
  /**
   * Chosen color for this dancer, as a KID_COLOR name (not raw hex). Drives
   * their chips across the family home, calendar and rosters. Absent means
   * "not chosen yet", and the original deterministic per-dancer color is
   * used instead, so nothing changes until someone picks one.
   */
  color?: string | null;
  /**
   * Server-mirrored Jr Staff designation (setStudentJrStaff and the Manage
   * Users sweep write it; families never do). True = this dancer is marked
   * Jr Staff, which is what lets the family home nudge the family to create
   * the dancer sign-in. Absent = not designated.
   */
  jrStaffDesignated?: boolean;
  /**
   * Set ONLY on a dancer a Jr Staff viewer may see by name alone (B2, Bobbie
   * 2026-08-23 and 2026-08-28: names studio-wide, full detail only for her
   * own classes). Such a record came from the jrStaffReads lane with no
   * birthday, guardians, siblings or photo on it, so a screen must not read
   * those empty fields as "none on file". Never set for any other viewer.
   */
  jrStaffNameOnly?: true;
  createdAt: Timestamp | FieldValue;
  updatedAt: Timestamp | FieldValue;
  createdBy: string;
}

/**
 * Save the color a dancer shows up as. Stores a palette NAME, and rules
 * accept only the known names, so a bad value cannot reach the doc. Pass null
 * to go back to the automatic color.
 */
export async function setDancerColor(studentId: string, color: string | null): Promise<void> {
  if (!db) throw new Error('Firestore not initialized.');
  await updateDoc(doc(db, 'students', studentId), {
    color,
    updatedAt: serverTimestamp(),
  });
}

/**
 * Upload a dancer profile photo and save its URL on the student doc.
 *
 * ONLY a linked family account. Active staff lost both halves of this on
 * 2026-09-05 (Bobbie: "Only the dancer and parents can change their profile
 * picture"), in the Storage rule and the Firestore rule together, so a caller
 * who is not in the student's familyUids is refused at the bytes as well as at
 * the URL save. The caller of this function is expected to have hidden its
 * control already; the docstring used to say "or active staff" and that is why
 * it is spelled out rather than deleted.
 *
 * NOT the dancer herself. An ordinary dancer sign-in is a server session with
 * no Firebase Auth principal, so she cannot reach this or any other client
 * write; her own lanes live in netlify/functions/dancerPortal.mjs, which has a
 * colour lane and no photo lane yet.
 */
export async function uploadDancerPhoto(studentId: string, uid: string, file: File): Promise<string> {
  if (!db) throw new Error('Firestore not initialized.');
  if (!storage) throw new Error('Firebase Storage not initialized.');
  if (!file.type.startsWith('image/')) throw new Error('Choose a photo (image file).');
  if (file.size >= 8 * 1024 * 1024) throw new Error('Photos need to be under 8 MB.');
  const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_') || 'dancer-photo';
  const path = `dancer-photos/${studentId}/${uid}/${Date.now()}-${safeName}`;
  const ref = storageRef(storage, path);
  await uploadBytes(ref, file, { contentType: file.type });
  const url = await getDownloadURL(ref);
  await updateDoc(doc(db, 'students', studentId), {
    photoUrl: url,
    updatedAt: serverTimestamp(),
  });
  return url;
}
function cleanString(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed || null;
}

/**
 * The video pipeline canary's fixture dancer (docs/video-robustness-plan-
 * 2026-08-18.md section 3) carries `canary: true` and must never render on
 * any all-students surface - Students.tsx, Search.tsx, ManageUsers,
 * CommandCenter, PrintRosters, TonightChecklist, Growth all read through
 * useStudents below, so filtering there is the one choke point that keeps a
 * synthetic nightly test dancer off every roster and picker in the Hub.
 * Mirror of isCanaryOfferingData in lib/offerings.ts.
 */
export function isCanaryStudentData(value: unknown): boolean {
  return Boolean(value && typeof value === 'object' && (value as Record<string, unknown>).canary === true);
}

function normalizeStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return Array.from(
    new Set(
      value
        .filter((item): item is string => typeof item === 'string')
        .map((item) => item.trim())
        .filter(Boolean)
    )
  );
}

export function normalizeStudentDoc(id: string, value: unknown): StudentDoc {
  const raw = value && typeof value === 'object' ? (value as Record<string, unknown>) : {};
  return {
    id,
    firstName: decodeHtmlEntities(cleanString(raw.firstName) ?? 'Dancer'),
    lastName: decodeHtmlEntities(cleanString(raw.lastName) ?? ''),
    ...(typeof raw.nickname === 'string' ? { nickname: decodeHtmlEntities(cleanString(raw.nickname) ?? '') } : {}),
    ...(typeof raw.preferredName === 'string' ? { preferredName: decodeHtmlEntities(cleanString(raw.preferredName) ?? '') } : {}),
    dob: (cleanString(raw.dob) ?? '') as IsoDate,
    joinedStudioOn: cleanString(raw.joinedStudioOn) as IsoDate | null,
    onCompTeam: raw.onCompTeam === true,
    familyUids: normalizeStringArray(raw.familyUids),
    siblingIds: normalizeStringArray(raw.siblingIds),
    notDuplicateIds: normalizeStringArray(raw.notDuplicateIds),
    memberships: normalizeStringArray(raw.memberships),
    photoUrl: cleanString(raw.photoUrl),
    color: cleanString(raw.color),
    ...(raw.jrStaffDesignated === true ? { jrStaffDesignated: true as const } : {}),
    createdAt: raw.createdAt as Timestamp | FieldValue,
    updatedAt: raw.updatedAt as Timestamp | FieldValue,
    createdBy: cleanString(raw.createdBy) ?? '',
  };
}
export interface UseStudentsResult {
  loading: boolean;
  students: StudentDoc[];
  error: string | null;
  /**
   * True until a snapshot for THIS listener has been confirmed by the
   * server. `loading:false` is not the same fact, for two separate reasons:
   * this hook sets loading false in its `!enabled` / `!uid` branch and never
   * sets it back to true when that flips (the ordinary cold-load path, since
   * a page's staff check only resolves once its user doc lands), and
   * Firestore's persistent cache can answer instantly with a copy that
   * predates a write made anywhere else. Either way an EMPTY list arrives
   * looking loaded, which is how "DANCERS ENROLLED 0" and "No dancers linked
   * yet" get printed over a studio full of dancers. Feed it to
   * lib/honestState.ts rather than reading it by hand.
   *
   * Only meaningful when `trackFromCache` is passed true; callers that do
   * not ask for it get the exact same subscription they always had. Starts
   * true, because nothing is confirmed until a snapshot says so.
   */
  fromCache: boolean;
}

/**
 * Subscribe to every student in the studio for active staff. Family views
 * should pass enabled=false unless they are using a family-scoped student hook.
 *
 * `trackFromCache` opts a caller into `includeMetadataChanges: true` so
 * `fromCache` reliably flips back to false even when the server confirms a
 * cached snapshot whose content did not change - without it a
 * cache-empty-then-server-empty roster never fires a second event and
 * `fromCache` sticks on true forever. Same parameter, same default and the
 * same reasoning as useOfferings in lib/offerings.ts.
 */
/** A jrStaffReads row as the StudentDoc every screen already takes. */
export function studentDocFromJrStaffRow(row: JrStaffStudentRow): StudentDoc {
  const student = normalizeStudentDoc(row.id, row.data);
  return row.detail === 'name' ? { ...student, jrStaffNameOnly: true } : student;
}

export function useStudents(enabled = true, trackFromCache = false): UseStudentsResult {
  const [students, setStudents] = useState<StudentDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fromCache, setFromCache] = useState(true);
  const readEpoch = useReadEpoch();
  const { user } = useAuth();
  // JR STAFF READ THROUGH THE SERVER (B2). The rules no longer give an
  // 'ensemble' account the whole collection: she gets every dancer's NAME and
  // full detail for her own classes from the jrStaffReads lane. The switch is
  // the rules' own answer: the ordinary listener runs for everyone, and only
  // a permission refusal sends this hook to the lane. So no role guess, no
  // extra user-doc listener for every other screen, and a Director's "view
  // as Jr Staff" preview keeps her real Director read. Every screen that
  // calls this hook gets the right answer with no change of its own.
  // Only a REAL Jr Staff goes to the lane (final review on B2): a family or
  // any other refusal keeps the plain error, with no lane call. Her real role
  // is read once, on the refusal (readRealRole), never as a listener here.
  const [refused, setRefused] = useState<{ uid: string; realRole: string | null } | null>(null);
  const viaLane = studentsReadViaLane({
    enabled,
    uid: user?.uid,
    refusedFor: refused?.uid ?? null,
    realRole: refused?.realRole,
  });
  const lane = useJrStaffStudentRows(viaLane ? user?.uid : null);
  const laneStudents = useMemo(
    () => lane.rows
      .map(studentDocFromJrStaffRow)
      .sort((a, b) => a.lastName.localeCompare(b.lastName) || a.firstName.localeCompare(b.firstName)),
    [lane.rows]
  );
  const listen = enabled && !viaLane;

  useEffect(() => {
    if (!listen) {
      setStudents([]);
      setError(null);
      setLoading(false);
      setFromCache(true);
      return;
    }
    if (!db) {
      setError('Firestore not initialized.');
      setLoading(false);
      setFromCache(true);
      return;
    }
    const q = query(collection(db, 'students'), orderBy('lastName'), orderBy('firstName'));
    // A role answer that lands after this listener is gone (unmount, another
    // sign-in, a Try again) belongs to a screen that no longer exists.
    let current = true;
    const unsub = onSnapshot(
      q,
      // includeMetadataChanges:false is the SDK default and byte-identical
      // to omitting the options object, so every pre-existing caller keeps
      // the exact event volume and timing it always had.
      { includeMetadataChanges: trackFromCache },
      (snap) => {
        setStudents(
          snap.docs
            .filter((d) => !isCanaryStudentData(d.data()))
            .map((d) => normalizeStudentDoc(d.id, d.data()))
        );
        // Clearing the error is what makes a retry mean anything. Without it
        // the resubscribe below delivers good data underneath a failed state
        // that never goes away.
        setError(null);
        setFromCache(snap.metadata.fromCache);
        setLoading(false);
      },
      (err) => {
        // A REFUSAL WAITS FOR THE REAL ROLE (refute of the final review's
        // fixes on B2). Until it is known the hook stays loading with no
        // error, so a real Jr Staff never sees the refusal flash before her
        // lane list arrives. Only a role that is not 'ensemble', or a role
        // read that fails (null), shows the refusal, exactly as before B2.
        if (isPermissionDenied(err) && user && db) {
          const uid = user.uid;
          const message = err.message;
          void readRealRole(db, uid).then((realRole) => {
            if (!current) return;
            if (realRole !== 'ensemble') {
              setError(message);
              setFromCache(true);
              setLoading(false);
            }
            setRefused({ uid, realRole });
          });
          return;
        }
        setError(err.message);
        setFromCache(true);
        setLoading(false);
      }
    );
    return () => {
      current = false;
      unsub();
    };
    // readEpoch: an onSnapshot error is terminal, so the only way back is to
    // build the listener again. The epoch only ever moves when a person
    // presses Try again or the network returns under a visible failed state,
    // so a healthy session never resubscribes because of it. See
    // lib/readEpoch.ts.
  }, [listen, trackFromCache, readEpoch, user]);

  if (viaLane) {
    return { loading: lane.loading, students: laneStudents, error: lane.error, fromCache: !lane.confirmed };
  }
  return { loading, students, error, fromCache };
}

/**
 * Subscribe only to students linked to a family account. This query matches
 * Firestore rules for parent access; do not use the all-students hook on
 * family-facing pages.
 */
export function useFamilyStudents(
  uid: string | null | undefined,
  trackFromCache = false
): UseStudentsResult {
  const [students, setStudents] = useState<StudentDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fromCache, setFromCache] = useState(true);
  const readEpoch = useReadEpoch();

  useEffect(() => {
    if (!uid) {
      setStudents([]);
      setError(null);
      setLoading(false);
      setFromCache(true);
      return;
    }
    if (!db) {
      setError('Firestore not initialized.');
      setLoading(false);
      setFromCache(true);
      return;
    }
    const q = query(collection(db, 'students'), where('familyUids', 'array-contains', uid));
    const unsub = onSnapshot(
      q,
      { includeMetadataChanges: trackFromCache },
      (snap) => {
        setStudents(
          snap.docs
            // The canary fixture's familyUids never contains a real parent's
            // uid, so this array-contains scope already excludes it in
            // practice; filtered anyway, belt and braces, matching
            // useStudents above. The live smoke test family is the one
            // viewer allowed through (lib/smokeFixture.ts), judged by who is
            // SIGNED IN, not by whose family this is: a Director's preview
            // of the robot's family still sees nothing.
            .filter((d) => !isHiddenFixtureData(d.data(), auth?.currentUser?.uid))
            .map((d) => normalizeStudentDoc(d.id, d.data()))
            .sort(
              (a, b) =>
                a.firstName.localeCompare(b.firstName) ||
                a.lastName.localeCompare(b.lastName)
            )
        );
        setError(null);
        setFromCache(snap.metadata.fromCache);
        setLoading(false);
      },
      (err) => {
        setError(err.message);
        setFromCache(true);
        setLoading(false);
      }
    );
    return () => unsub();
    // readEpoch: see useStudents above. A dead listener only comes back by
    // being built again.
  }, [uid, trackFromCache, readEpoch]);

  return { loading, students, error, fromCache };
}

export interface NewStudentInput {
  firstName: string;
  lastName: string;
  dob: IsoDate;
  joinedStudioOn: IsoDate | null;
  onCompTeam: boolean;
  familyUids: string[];
}

export function useCreateStudent() {
  const { user } = useAuth();
  return async (input: NewStudentInput): Promise<string> => {
    if (!db) throw new Error('Firestore not initialized.');
    if (!user) throw new Error('Sign in required.');
    const ref = await addDoc(collection(db, 'students'), {
      firstName: decodeHtmlEntities(input.firstName.trim()),
      lastName: decodeHtmlEntities(input.lastName.trim()),
      dob: input.dob,
      joinedStudioOn: input.joinedStudioOn,
      onCompTeam: input.onCompTeam,
      familyUids: input.familyUids,
      siblingIds: [],
      memberships: [],
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      createdBy: user.uid,
    });
    return ref.id;
  };
}

export async function updateStudent(
  id: string,
  fields: Partial<Omit<StudentDoc, 'id' | 'createdAt' | 'createdBy'>>
): Promise<void> {
  if (!db) throw new Error('Firestore not initialized.');
  await updateDoc(doc(db, 'students', id), {
    ...fields,
    ...Object.fromEntries(
      ['firstName', 'lastName', 'nickname', 'preferredName']
        .filter((field) => typeof fields[field as keyof typeof fields] === 'string')
        .map((field) => [field, decodeHtmlEntities(fields[field as keyof typeof fields] as string)])
    ),
    updatedAt: serverTimestamp(),
  });
}

/**
 * Subscribe to a single student doc by id. Works for Director, active staff,
 * and family members linked via familyUids[] (per Firestore rules).
 */
export function useStudentById(id: string | null | undefined): {
  loading: boolean;
  student: StudentDoc | null;
  error: string | null;
} {
  const [student, setStudent] = useState<StudentDoc | null>(null);
  const [loadedId, setLoadedId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // A Jr Staff reads one dancer through the same lane as the list (B2): full
  // for her own classes and her own family, a name card for anybody else, and
  // nothing for an id the studio does not have.
  // Same switch as useStudents: only the rules' refusal of this one dancer
  // sends the hook to the lane.
  const { user } = useAuth();
  const [refused, setRefused] = useState<{ key: string; uid: string; realRole: string | null } | null>(null);
  const viaLane = studentsReadViaLane({
    enabled: !!id,
    uid: user?.uid,
    refusedFor: user && refused?.key === `${user.uid}:${id}` ? refused.uid : null,
    realRole: refused?.realRole,
  });
  const lane = useJrStaffStudentRows(viaLane ? user?.uid : null);
  const listen = !!id && !viaLane;
  useEffect(() => {
    if (!db || !id || !listen) {
      setStudent(null);
      setLoadedId(null);
      setError(null);
      setLoading(false);
      return;
    }
    // Do not carry one dancer across an in-app route change while the next
    // listener is opening. The return guard below also closes the pre-effect
    // render window where React still holds the previous state value.
    setStudent(null);
    setLoadedId(null);
    setError(null);
    setLoading(true);
    // Same guard as useStudents: a late role answer for a closed listener is ignored.
    let current = true;
    const unsub = onSnapshot(
      doc(db, 'students', id),
      (snap) => {
        setStudent(
          snap.exists()
            ? normalizeStudentDoc(snap.id, snap.data())
            : null
        );
        setLoadedId(id);
        setLoading(false);
      },
      (err) => {
        // Same wait as useStudents: loading, no error, until the real role
        // is known; the refusal shows only for a role that is not 'ensemble'.
        if (isPermissionDenied(err) && user && db) {
          const uid = user.uid;
          const key = `${uid}:${id}`;
          const message = err.message;
          void readRealRole(db, uid).then((realRole) => {
            if (!current) return;
            if (realRole !== 'ensemble') {
              setError(message);
              setLoadedId(id);
              setLoading(false);
            }
            setRefused({ key, uid, realRole });
          });
          return;
        }
        setError(err.message);
        setLoadedId(id);
        setLoading(false);
      }
    );
    return () => {
      current = false;
      unsub();
    };
  }, [id, listen, user]);
  const laneStudent = useMemo(() => {
    const row = viaLane ? lane.rows.find((r) => r.id === id) : undefined;
    return row ? studentDocFromJrStaffRow(row) : null;
  }, [viaLane, lane.rows, id]);
  if (viaLane) return { loading: lane.loading, student: laneStudent, error: lane.error };
  const currentStudent = loadedId === id && student?.id === id ? student : null;
  return { loading: loading || (!!id && loadedId !== id), student: currentStudent, error };
}

/**
 * Delete a dancer, AND take them off every roster first.
 *
 * This used to be one line, `deleteDoc(doc(db,'students',id))`, and that one
 * line is the 2026-08-15 audit's H3. Deleting a dancer who was still on
 * classes left their id sitting in every `offering.studentIds` with no
 * document behind it: the class counted a phantom in its headcount, a full
 * class reported zero open spots, the teacher's register read "1 unmarked"
 * every night with no name and no row to tap, and Tonight's checklist could
 * never complete. Every repair path then failed, because they all came back
 * through setEnrollment, which refused with "student not found".
 *
 * MEMBERSHIPS ARE THE LINKING TRUTH AND BOTH SIDES MOVE TOGETHER. That is
 * this repo's standing law for enrollment, and a delete is an enrollment
 * change like any other: it ends every membership this dancer has. So the
 * memberships are ended THROUGH THE SAME SERVER TRANSACTION the roster editor
 * uses, one per class, and only then is the document removed.
 *
 * Two consequences worth stating, because both are deliberate:
 *
 *   IT IS NOT ATOMIC ACROSS CLASSES, and it does not need to be. Each
 *   unenroll is atomic on its own, and the ORDER is what makes a partial run
 *   safe: if the third unenroll fails, the dancer still exists, still holds
 *   the memberships that did not clear, and nothing is a ghost. The delete is
 *   the last thing that happens, and it does not happen at all unless every
 *   roster is already clear. Failing loudly with the dancer intact is the
 *   good outcome here; the bad one is a document deleted out from under three
 *   rosters.
 *
 *   IT TRIES EVERY CLASS BEFORE IT GIVES UP, and then names the ones that
 *   refused. It used to throw on the first failure, which meant a Director
 *   got one error, no idea which class caused it, and no progress: classes
 *   four and five were never even attempted, so a second attempt hit exactly
 *   the same wall. Now the loop runs to the end, so every roster that CAN be
 *   cleared IS cleared and each attempt gets strictly closer, and the error
 *   lists the classes BY NAME with what to do about them. What did NOT
 *   change is the refusal itself: one unclearable roster still cancels the
 *   delete, because continuing on to deleteDoc is precisely how the 2026-08-15
 *   ghost was made. Continue past the failure, refuse at the end. This is
 *   deliberately not the shape of the term-trim bulk action that aborted
 *   everything on one refusal and reported "stopped after 0".
 *
 *   IT READS THE DANCER FIRST rather than trusting the caller's copy. A
 *   screen that has been open a while can be holding a membership list from
 *   before somebody else enrolled them, and a stale list is exactly how one
 *   roster gets missed.
 *
 *   A LINK TO A CLASS THAT NO LONGER EXISTS IS ALREADY RELEASED, and is
 *   swept rather than refused. Deleting a class removes only the class side
 *   of the link, so every dancer who was on it keeps an id pointing at
 *   nothing; the unenroll lane clears the dancer's side and writes no
 *   offering (setEnrollment.mjs says why at the guard). That is what made
 *   Bobbie's demo dancers undeletable: every attempt came back "offering not
 *   found" from a class she had already deleted on purpose. The result
 *   reports the two kinds separately, because "taken off a live roster" and
 *   "swept up after a class that was already gone" are different events.
 *
 *   AND IT ASKS THE ROSTERS THEMSELVES, not only the dancer's membership
 *   list. Both sides of the link are queried and the union is cleared, so a
 *   class whose studentIds still names this dancer is taken care of even when
 *   her memberships never mentioned it. Details at the query below, including
 *   why it carries no orderBy.
 *
 * The server unenroll also refreshes the family's costume and calendar
 * visibility snapshots, so a departed family stops seeing a class they are no
 * longer part of. That was a second, separate finding in the same audit.
 *
 * AND IT REVOKES THE DANCER'S JR STAFF ACCESS AFTER THE READS AND BEFORE
 * ANYTHING IRREVERSIBLE, for the same reason it unenrolls before deleting.
 *
 * Deleting the students doc used to touch nothing else. Under Jr Staff
 * parity that leaves a live STAFF session behind: a promoted dancer login's
 * users doc reads {ensemble, active} and survives the delete, and every door
 * that could take it back reads the students doc that just went away. The
 * un-designate lane 404s, the parent's own Disable button 403s, and Manage
 * Users refuses the account as a managed dancer identity. Firebase refresh
 * tokens do not expire, so nothing inside this app could ever end that
 * session again; only the Firebase console could, and that is the owner's
 * alone.
 *
 * The ORDER inside the function follows one rule: every READ that can refuse
 * runs first, with her designation still intact, so a refusal at the dancer
 * read or the roster read leaves a dancer who is exactly as she was, Jr
 * Staff access included, and the sentence she is told says so. Only once
 * both reads have succeeded does the revoke run, through the SERVER lane
 * that demotes the login back to a plain dancer sign-in and revokes its
 * refresh tokens (setStudentJrStaff.mjs), and it runs BEFORE the unenroll
 * loop and before deleteDoc, because both of those are the irreversible part
 * and the revoke must land before either. Same ordering discipline as the
 * unenrolls, same reason: if the revoke fails, the dancer still exists,
 * still holds her designation, and every door is still open. Failing loudly
 * with the dancer intact is the good outcome. It is idempotent, so a dancer
 * who was never Jr Staff pays one no-op call and nothing else.
 */
export interface DeleteStudentResult {
  /** Live classes this dancer was taken off, by offering id. */
  releasedOfferingIds: string[];
  /**
   * Links to classes that no longer existed. The SERVER decides which bucket
   * each id lands in (setEnrollment reports offeringAlreadyDeleted), never the
   * page's own copy of the catalog, so the sentence she reads afterwards is
   * what actually happened rather than what a screen guessed.
   */
  deadLinkOfferingIds: string[];
}

export async function deleteStudent(id: string): Promise<DeleteStudentResult> {
  if (!db) throw new Error('Firestore not initialized.');
  const snap = await getDoc(doc(db, 'students', id));
  const dancer = snap.exists() ? normalizeStudentDoc(snap.id, snap.data()) : null;
  // Falls back on a BLANK name, not only on a missing document. An imported
  // record with both names empty trimmed to '' and every sentence below opened
  // with a space: " is still on a class that would not let go". Final review
  // 2026-09-09.
  const dancerName = (dancer ? `${dancer.firstName} ${dancer.lastName}`.trim() : '') || 'This dancer';
  // A CACHE-ONLY ANSWER IS AN UNKNOWN, NOT AN ANSWER. getDoc and getDocs both
  // fall back to the local cache when the server cannot be reached, and they
  // do it WITHOUT THROWING: the snapshot simply comes back with
  // metadata.fromCache true. Every honest-state surface on the pages that call
  // this already refuses to make a claim from that (lib/honestState.ts); this
  // write path is the one that has to refuse to ACT on it. Adversarial review
  // 2026-09-07 proved the hole: offline, a stale cached dancer with an empty
  // memberships list plus an empty cached roster query means the unenroll loop
  // runs zero times, no network call is ever attempted, and the document
  // removal is queued locally and lands on reconnect - taking a dancer out
  // while live rosters still name her, which is exactly the ghost this
  // function exists to prevent. Until this PR the missing-offering throw was the accidental
  // circuit breaker for it; tolerating a deleted class removes that, so the
  // check is made on purpose here instead.
  if (snap.metadata.fromCache) {
    throw new Error(
      `${dancerName}'s record could not be re-read from the server, so the dancer was not `
      + 'deleted. This device is working from a saved copy, which cannot say which classes '
      + 'they are really on. Nothing was changed. Check the connection and try again.'
    );
  }
  const memberships = dancer?.memberships ?? [];
  // ASK THE ROSTERS TOO, DO NOT TRUST THE MEMBERSHIP LIST ALONE.
  //
  // memberships is one side of a two-sided link, and this whole function
  // exists because the two sides can disagree. Clearing only what the dancer's
  // own list names is exactly the shape of the bug above, pointed the other
  // way: a live class whose studentIds still holds this id, with no membership
  // saying so, was never even attempted, and the delete went through on top of
  // it. That is "a document deleted out from under three rosters", which this
  // function's own reasoning calls the bad outcome.
  //
  // It used to be covered by accident. An unresolvable membership id made the
  // unenroll throw, which refused the delete outright, so a half-broken link
  // graph could not be deleted at all. Tolerating a deleted class removes that
  // accidental circuit breaker, so the roster side is now asked directly.
  //
  // ONE array-contains CLAUSE AND NO orderBy, deliberately: a single-field
  // array query needs only Firestore's automatic index, and the emulator does
  // NOT enforce composite indexes, so a where + orderBy added here would pass
  // every test and 400 in production (that gap produced a dead button two days
  // ago). Same query shape the server already uses in
  // netlify/functions/lib/classFamilyAudience.mjs.
  let rosterOfferingIds: string[] = [];
  try {
    const rosterSnap = await getDocs(
      query(collection(db, 'offerings'), where('studentIds', 'array-contains', id))
    );
    // Same reading as the dancer read above, and for the same reason: an
    // offline getDocs answers from the cache with no error at all, and an
    // empty cached roster result is indistinguishable from "nobody has her on
    // a roster" unless this is checked.
    if (rosterSnap.metadata.fromCache) {
      throw new Error('the class list came from this device\'s saved copy, not the server');
    }
    rosterOfferingIds = rosterSnap.docs.map((d) => d.id);
  } catch (e) {
    // A read that failed tells us nothing about who is on which roster, and
    // deleting on top of an unknown is the thing this refuses to do. The Jr
    // Staff revoke runs AFTER this read succeeds, not before, so a failure
    // here has not touched her access at all.
    const why = e instanceof Error ? e.message : String(e);
    throw new Error(
      `${dancerName}'s class rosters could not be checked, so the dancer was not deleted. `
      + 'Nothing was taken off any roster and her Jr Staff access is unchanged. '
      + `Try again in a moment. (${why})`
    );
  }
  // Both reads succeeded, so this is the last point before anything
  // irreversible: the revoke runs here, before the unenroll loop and before
  // the document removal below, same reasoning as the comment above the
  // function.
  try {
    await setStudentJrStaffDesignation(id, false);
  } catch (e) {
    const why = e instanceof Error ? e.message : String(e);
    throw new Error(
      'This dancer\'s Jr Staff access could not be taken back, so nothing was deleted. '
      + `Deleting now would leave a staff sign-in nothing here could switch off. (${why})`
    );
  }
  const blocked: { offeringId: string; why: string }[] = [];
  const releasedOfferingIds: string[] = [];
  const deadLinkOfferingIds: string[] = [];
  for (const offeringId of Array.from(new Set([...memberships, ...rosterOfferingIds]))) {
    try {
      // The server answers with which of the two it did. A class that was
      // already deleted is not a failure and never was a roster the dancer
      // could be "taken off": the link is swept and counted separately, so
      // "taken off 1 class" and "2 links to classes that no longer exist were
      // cleared" stay tellable apart in the sentence she reads.
      const result = await unenrollStudent(offeringId, id);
      if (result.offeringAlreadyDeleted) deadLinkOfferingIds.push(offeringId);
      else releasedOfferingIds.push(offeringId);
    } catch (e) {
      blocked.push({ offeringId, why: e instanceof Error ? e.message : String(e) });
    }
  }
  if (blocked.length > 0) {
    const names = await Promise.all(
      blocked.map(async ({ offeringId }) => {
        try {
          const o = await getDoc(doc(db!, 'offerings', offeringId));
          const name = o.exists() ? cleanString(o.data()?.name) : null;
          return name || offeringId;
        } catch {
          return offeringId;
        }
      })
    );
    // "The dancer was not deleted", not "nothing was deleted": by the time
    // this throws, the Jr Staff designation is already off and every class
    // that COULD be cleared already is. Saying nothing happened would be the
    // same false reassurance the Delete confirm used to give (Students.tsx).
    //
    // The Jr Staff sentence below is appended ONLY when she was actually
    // designated. The revoke above is a no-op for a dancer who was never Jr
    // Staff, and telling every Director "Jr Staff access was turned off" on a
    // delete that blocked is false for the dancer it does not apply to; read
    // from the same normalized `dancer` the reads above already confirmed,
    // never re-guessed from the caller's own copy.
    const jrStaffClause = dancer?.jrStaffDesignated === true
      ? ' Jr Staff access was turned off before the classes were cleared; turn it back on from '
        + 'her card if she is staying.'
      : '';
    throw new Error(
      `${dancerName} is still on ${names.length === 1 ? 'a class' : `${names.length} classes`} `
      + `that would not let go, so the dancer was not deleted: ${names.join(', ')}. `
      + `Every other class is already clear, so open ${names.length === 1 ? 'that class' : 'those classes'}, `
      + `take them off the roster there, then delete again. (${blocked[0].why})`
      + jrStaffClause
    );
  }
  await deleteDoc(doc(db, 'students', id));
  // IDEMPOTENT ON PURPOSE. A second call on an id that is already gone reads
  // an empty dancer, finds no roster naming it, clears nothing and deletes a
  // document that is not there, which Firestore treats as a no-op. So a
  // double-tap, a retry after a timeout, or two Directors on the same dancer
  // all end in the same place instead of an error that says something broke.
  return { releasedOfferingIds, deadLinkOfferingIds };
}

/** Mark two students as siblings — adds each ID to the other's siblingIds[]. */
export async function linkSiblings(idA: string, idB: string): Promise<void> {
  if (!db) throw new Error('Firestore not initialized.');
  const batch = writeBatch(db);
  batch.update(doc(db, 'students', idA), {
    siblingIds: arrayUnion(idB),
    updatedAt: serverTimestamp(),
  });
  batch.update(doc(db, 'students', idB), {
    siblingIds: arrayUnion(idA),
    updatedAt: serverTimestamp(),
  });
  await batch.commit();
}

/** Remove a sibling link between two students — removes each ID from the other's siblingIds[]. */
export async function unlinkSibling(idA: string, idB: string): Promise<void> {
  if (!db) throw new Error('Firestore not initialized.');
  const batch = writeBatch(db);
  batch.update(doc(db, 'students', idA), {
    siblingIds: arrayRemove(idB),
    updatedAt: serverTimestamp(),
  });
  batch.update(doc(db, 'students', idB), {
    siblingIds: arrayRemove(idA),
    updatedAt: serverTimestamp(),
  });
  await batch.commit();
}

/**
 * Record that two students are NOT the same person - adds each ID to the
 * other's notDuplicateIds[], the exact bidirectional shape linkSiblings
 * uses. Director only (the Possible duplicates card is Director-gated and
 * the students update rule leaves this field to the Director's full-write
 * lane). Deliberately NOT part of useCreateStudent: new docs simply omit
 * the field, so this write is the only place it appears and a client that
 * ships ahead of the rules deploy breaks nothing but this one new button.
 */
export async function markNotDuplicates(idA: string, idB: string): Promise<void> {
  if (!db) throw new Error('Firestore not initialized.');
  const batch = writeBatch(db);
  batch.update(doc(db, 'students', idA), {
    notDuplicateIds: arrayUnion(idB),
    updatedAt: serverTimestamp(),
  });
  batch.update(doc(db, 'students', idB), {
    notDuplicateIds: arrayUnion(idA),
    updatedAt: serverTimestamp(),
  });
  await batch.commit();
}

/** Undo markNotDuplicates - the pair returns to the duplicates card. */
export async function unmarkNotDuplicates(idA: string, idB: string): Promise<void> {
  if (!db) throw new Error('Firestore not initialized.');
  const batch = writeBatch(db);
  batch.update(doc(db, 'students', idA), {
    notDuplicateIds: arrayRemove(idB),
    updatedAt: serverTimestamp(),
  });
  batch.update(doc(db, 'students', idB), {
    notDuplicateIds: arrayRemove(idA),
    updatedAt: serverTimestamp(),
  });
  await batch.commit();
}

/**
 * Add or remove one guardian link (familyUids), touching ONLY that field
 * plus updatedAt - same narrow shape as linkSiblings/unlinkSibling above.
 * Bobbie: "I don't want them to be able to edit siblings, just the
 * director and admin staff should" - the same scoped-write pattern lets
 * Firestore rules grant Director + Admin (foh) this one field without
 * opening the rest of the dancer's profile to them.
 *
 * ADDING GOES THROUGH THE SERVER since bag group plan BG-3 (N2). The browser
 * write it used to be told no server code that a parent had been linked, so a
 * bag group already carrying the dancer never gained her. familyLink.mjs's
 * `add` action makes the same one-field write, then adds her to those groups
 * and tells her once. The Director and Admin rules lane is unchanged; this
 * screen just no longer uses it.
 *
 * Resolves to a sentence for the screen when a bag group was too full to take
 * her (the link itself is saved), or null.
 */
export async function addFamilyLink(studentId: string, uid: string): Promise<string | null> {
  const me = auth?.currentUser;
  if (!me) throw new Error('Sign in again before linking a family account.');
  const response = await fetch('/.netlify/functions/familyLink', {
    method: 'POST',
    headers: await functionHeaders(me),
    body: JSON.stringify({ action: 'add', studentId, uid }),
  });
  const body = await response.json().catch(() => ({})) as {
    error?: string;
    bagGroupNotice?: string;
  };
  if (!response.ok) throw new Error(familyLinkAddErrorText(body.error));
  return typeof body.bagGroupNotice === 'string' && body.bagGroupNotice ? body.bagGroupNotice : null;
}

/** Plain words for the add lane's refusals. */
function familyLinkAddErrorText(code: string | undefined): string {
  if (code === 'too_many_guardians') return 'A dancer can have up to 20 linked accounts. Remove one before adding another.';
  if (code === 'front_desk_self_link') return 'Only a Manager or the Director can link their own account to a dancer.';
  if (code === 'user_not_found') return 'That account no longer exists. Refresh and try again.';
  if (code === 'student_not_found') return 'That dancer no longer exists. Refresh and try again.';
  if (code === 'family_unlink_in_progress') return 'That account is still being unlinked. Try again in a minute.';
  if (code === 'director_or_admin_only') return 'Only the Director, Admin or a Manager can link a family account.';
  if (code === 'too_many_siblings') return 'This dancer has more linked siblings than one sync can take. Check the sibling list, then try again.';
  return code || 'Could not link that family account.';
}

/**
 * The two removal notices that are warnings rather than information: the link
 * came off but her bag groups could not be updated, or a full group refused
 * to change. They match BAG_GROUP_UNLINK_FAILED_NOTICE and
 * BAG_GROUP_UNLINK_FULL_NOTICE_TAIL in netlify/functions/lib/bagGroupUnlinkSync.mjs
 * (a test pins them together). Every other removal notice is a "kept" line:
 * the unlink worked and a group kept her on purpose.
 */
export const BAG_GROUP_UNLINK_FAILED_NOTICE =
  'The link is removed, but her bag groups could not be updated. Remove her in Manage group.';
export const BAG_GROUP_UNLINK_FULL_NOTICE_TAIL =
  ' is full, so a parent unlinked from one of its dancers is still in it. Remove that parent in Manage group.';

/** True when a removal notice carries a failure or refusal line, so it stays warning-styled. */
export function bagGroupNoticeIsWarning(notice: string): boolean {
  return notice.includes(BAG_GROUP_UNLINK_FAILED_NOTICE) || notice.includes(BAG_GROUP_UNLINK_FULL_NOTICE_TAIL);
}

/**
 * Remove one guardian link through the server, which also takes her out of
 * the dancer's bag groups where she has no other linked dancer (plan Q10,
 * DECISIONS 133 item 1). Resolves to a sentence for the screen when a group
 * kept her (she leads it, or the account is studio staff or a Jr Staff
 * sign-in), or null.
 */
export async function removeFamilyLink(studentId: string, uid: string): Promise<string | null> {
  const me = auth?.currentUser;
  if (!me) throw new Error('Sign in again before removing a family link.');
  for (let pass = 0; pass < 100; pass += 1) {
    const response = await fetch('/.netlify/functions/familyLink', {
      method: 'POST',
      headers: await functionHeaders(me),
      body: JSON.stringify({ action: 'remove', studentId, uid }),
    });
    const body = await response.json().catch(() => ({})) as {
      error?: string;
      inProgress?: boolean;
      bagGroupNotice?: string;
    };
    if (!response.ok) throw new Error(body.error || 'Could not remove that family link.');
    if (body.inProgress !== true) {
      return typeof body.bagGroupNotice === 'string' && body.bagGroupNotice ? body.bagGroupNotice : null;
    }
  }
  throw new Error('That family link is safely blocked, but its older class history still needs cleanup. Try Remove again.');
}

/** One dancer's part in a family-account sync, for the plain-words result. */
export interface SyncFamilyDancerResult {
  id: string;
  name: string;
  /** True only when this dancer's stored family links actually changed. */
  changed: boolean;
}

/**
 * What syncSiblingFamilies actually did, so the button can report real
 * numbers instead of a bare "done". This action only touches the
 * students/{id}.familyUids field; it does not re-snapshot class rosters,
 * costume cards, or calendar recipient lists, so the result says that
 * plainly rather than implying a wider sync happened. The one thing it does
 * carry on with is bag groups: a guardian newly linked to a sibling joins
 * that sibling's bag groups (bag group plan BG-3), and a group too full to
 * take her comes back as bagGroupNotice, in words for the screen.
 */
export interface SyncFamilyResult {
  dancers: SyncFamilyDancerResult[];
  checkedCount: number;
  updatedCount: number;
  bagGroupNotice?: string | null;
}

/**
 * Merge family UIDs across a sibling group. Every familyUid from the anchor
 * student and all their linked siblings ends up on every student in the group,
 * so all siblings share the same parent/guardian accounts. Returns which
 * dancers were checked and which ones actually changed, so the caller can
 * show real numbers.
 *
 * RUNS ON THE SERVER since bag group plan BG-3 (familyLink.mjs, action
 * `syncSiblings`). The browser batch it used to be was a guardian link no
 * server code ever saw, so a parent it linked to a sibling never joined that
 * sibling's bag groups. The server reads every sibling fresh in one
 * transaction, adds only (never replaces), holds the rules lane's lines, and
 * then offers each newly linked guardian to that sibling's groups. Names for
 * the result still come from the roster this screen already has.
 */
export async function syncSiblingFamilies(
  anchorId: string,
  allStudents: StudentDoc[]
): Promise<SyncFamilyResult> {
  const me = auth?.currentUser;
  if (!me) throw new Error('Sign in again before syncing family accounts.');
  const response = await fetch('/.netlify/functions/familyLink', {
    method: 'POST',
    headers: await functionHeaders(me),
    body: JSON.stringify({ action: 'syncSiblings', studentId: anchorId }),
  });
  const body = await response.json().catch(() => ({})) as {
    error?: string;
    dancers?: Array<{ id?: unknown; changed?: unknown }>;
    checkedCount?: unknown;
    updatedCount?: unknown;
    bagGroupNotice?: unknown;
  };
  if (!response.ok) throw new Error(familyLinkAddErrorText(body.error));
  const byId = new Map(allStudents.map((student) => [student.id, student]));
  const dancers: SyncFamilyDancerResult[] = (Array.isArray(body.dancers) ? body.dancers : [])
    .filter((row): row is { id: string; changed?: unknown } => typeof row?.id === 'string')
    .map((row) => {
      const student = byId.get(row.id);
      return {
        id: row.id,
        name: student ? `${student.firstName} ${student.lastName}`.trim() : 'Dancer',
        changed: row.changed === true,
      };
    });
  return {
    dancers,
    checkedCount: typeof body.checkedCount === 'number' ? body.checkedCount : dancers.length,
    updatedCount: typeof body.updatedCount === 'number'
      ? body.updatedCount
      : dancers.filter((dancer) => dancer.changed).length,
    bagGroupNotice: typeof body.bagGroupNotice === 'string' && body.bagGroupNotice ? body.bagGroupNotice : null,
  };
}

/**
 * Hook returning a bulk-create function bound to the current Director.
 * Splits into 500-op batches (Firestore's hard cap) and commits each
 * batch sequentially so a network blip after the first chunk still
 * leaves the database in a sane partial-success state.
 *
 * Returns the number of students written.
 */
export function useBulkCreateStudents() {
  const { user } = useAuth();
  return async (inputs: NewStudentInput[]): Promise<number> => {
    if (!db) throw new Error('Firestore not initialized.');
    if (!user) throw new Error('Sign in required.');
    let written = 0;
    const CHUNK = 500;
    for (let i = 0; i < inputs.length; i += CHUNK) {
      const slice = inputs.slice(i, i + CHUNK);
      const batch = writeBatch(db);
      for (const input of slice) {
        const ref = doc(collection(db, 'students'));
        batch.set(ref, {
          firstName: decodeHtmlEntities(input.firstName.trim()),
          lastName: decodeHtmlEntities(input.lastName.trim()),
          dob: input.dob,
          joinedStudioOn: input.joinedStudioOn,
          onCompTeam: input.onCompTeam,
          familyUids: input.familyUids,
          siblingIds: [],
          memberships: [],
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          createdBy: user.uid,
        });
      }
      await batch.commit();
      written += slice.length;
    }
    return written;
  };
}
